import { createTableV2RenderBinding } from '@databases/frontend/table-v2';
import { type TableV2RenderSnapshot } from '@databases/frontend/table-v2-host';
import {
  type TableV2Instance,
  type TableV2Reads,
} from '@databases/view-layout-table-v2/data';
import { scrollTableV2CellIntoView } from '@databases/view-layout-table-v2/dom';
import { TABLE_V2_LAYOUT_ID } from '@databases/view-layout-table-v2/id';
import { type TableV2PresenceDependencies } from '../bootstrap/initialize-table-v2-presence.ts';
import { createTableV2MouseInputAdapter } from '../features/selections/create-table-v2-mouse-input-adapter.ts';
import { TableV2SelectionDatabaseAdapter } from '../features/selections/table-v2-selection-database-adapter.ts';
import { createViewProjection } from './projection/create-view-projection.ts';
import { type SelectionLayoutDependencies } from './selection-layout-dependencies.ts';

/**
 * One V2 view's source ordering and committed presence placement.
 * Its lifetime follows the database and view, not React mounts or permission
 * changes. Disposal revokes its lazy entry readers; it does not dispose the SDK,
 * selection machine or highlights controller.
 */
export interface TableV2ViewSession {
  readonly renderSnapshot: TableV2RenderSnapshot;
  readonly presenceDependencies: TableV2PresenceDependencies;
  readonly selectionDependencies: SelectionLayoutDependencies;
  dispose(): void;
}

/**
 * The rendering binding stays mounted across structural publications. Its layout
 * effect publishes only presence placement after child geometry commits. Selection
 * reconciliation reads and observes source ordering immediately, including before
 * lazy rendering mounts. Both projections are disposable views of the same source;
 * neither owns canonical data. Obsolete render commits cannot replace placement.
 */
export function createTableV2ViewSession({
  source,
  contentId,
  getSpaceKey,
  subscribePermissions,
}: {
  contentId?: string;
  source: {
    getSnapshot(): Omit<TableV2Instance, 'dispose'> | undefined;
    subscribe(listener: VoidFunction): VoidFunction;
    dispose(): void;
  };
  getSpaceKey: () => string;
  subscribePermissions?: (listener: VoidFunction) => VoidFunction;
}): TableV2ViewSession {
  let committedTable = source.getSnapshot();
  let disposed = false;
  let invalidateProjection: VoidFunction;

  const projection = createViewProjection({
    read: () =>
      !disposed && committedTable
        ? {
            entryIds: committedTable.projection.entryIds,
            fieldIds: committedTable.projection.visibleFieldIds,
          }
        : undefined,
    subscribeInvalidation(listener) {
      invalidateProjection = listener;
      return () => {};
    },
  });

  const readCurrent = () => (disposed ? undefined : source.getSnapshot());
  const semanticProjection = createViewProjection({
    read() {
      const current = readCurrent()?.projection;
      return current
        ? { entryIds: current.entryIds, fieldIds: current.visibleFieldIds }
        : undefined;
    },
    subscribeInvalidation: source.subscribe,
  });

  const databaseAdapter = new TableV2SelectionDatabaseAdapter({
    get projection() {
      return readCurrent()?.projection;
    },
    getEntry: (id) => readCurrent()?.getEntry(id),
    getSpaceKey,
  });

  const reads: TableV2Reads = {
    getValue: (address) => readCurrent()?.getValue(address),
    getDefaultValue: (id) => readCurrent()?.getDefaultValue(id),
  };

  let resourceController = new AbortController();
  const resourceListeners = new Set<VoidFunction>();

  const unsubscribePermissions = subscribePermissions?.(() => {
    if (disposed) {
      return;
    }

    resourceController.abort();
    resourceController = new AbortController();

    for (const listener of resourceListeners) {
      listener();
    }
  });

  const resources = {
    getSnapshot: () => resourceController.signal,
    subscribe(listener: VoidFunction) {
      resourceListeners.add(listener);

      return () => {
        resourceListeners.delete(listener);
      };
    },
  };

  return {
    renderSnapshot: createTableV2RenderBinding(
      {
        getSnapshot: source.getSnapshot,
        subscribe: source.subscribe,
        reads,
        commit(next) {
          if (disposed || next !== source.getSnapshot()) {
            return;
          }

          committedTable = next;
          invalidateProjection();
        },
      },
      contentId,
      resources,
    ),
    presenceDependencies: projection,
    selectionDependencies: {
      databaseAdapter,
      viewChanges: {
        isAvailable: () => semanticProjection.getSnapshot().status === 'ready',
        subscribe: semanticProjection.subscribe,
      },
      mouseInputAdapter: createTableV2MouseInputAdapter(),
      canEditContent: false,
      featureLayoutTypes: [TABLE_V2_LAYOUT_ID],
      selectionUiMetricsEnabled: false,
      scrollCellIntoView(cell) {
        const scrollContainer = document.getElementById('scrollable-inner-container');

        if (scrollContainer) {
          scrollTableV2CellIntoView(scrollContainer, cell.row.index, cell.col.index);
        }
      },
    },
    dispose: () => {
      if (disposed) {
        return;
      }

      disposed = true;
      unsubscribePermissions?.();
      resourceController.abort();
      resourceListeners.clear();
      semanticProjection.dispose();
      projection.dispose();
      source.dispose();
      committedTable = undefined;
    },
  };
}
